import { createSelector } from '@reduxjs/toolkit';

import { RootState } from './store';

// Basic selectors
export const selectCurrentTabIndex = (state: RootState) => state.navigation.currentTabIndex;

// Affordable Document selectors
export const selectAffordableDocumentState = (state: RootState) => state.affordableDocument;
export const selectAffordableDocuments = (state: RootState) => state.affordableDocument.documents;
export const selectAffordableDocumentLoading = (state: RootState) =>
  state.affordableDocument.isLoading;
export const selectAffordableDocumentError = (state: RootState) => state.affordableDocument.error;

export const selectActiveStepByTab = (state: RootState) => state.navigation.activeStepByTab;
export const selectStepStatusByTab = (state: RootState) => state.navigation.stepStatus;
export const selectLeadProfileApplicants = (state: RootState) => state.leadProfile.applicants;
export const selectIsLeadProfile = (state: RootState) => state.auth.isLeadProfile;

/** Stable fallback so input selectors stay referentially stable for memoized selectors (e.g. createSelector). */
const EMPTY_BRANDING_MEDIA = {};

export const selectBrandingMedia = (state: RootState) =>
  state.branding?.media ?? EMPTY_BRANDING_MEDIA;
export const selectResponse = (state: RootState) => state.response;
export const selectApplication = (state: RootState) => state.application;
export const selectPropertyId = (state: RootState) => state.application.applicant?.propertyId;
export const selectIsPreviewMode = (state: RootState) => state.auth?.isPreviewMode ?? false;

// Selectors for multi-applicant navigation
export const selectActiveStep = createSelector(
  [selectActiveStepByTab, selectCurrentTabIndex],
  (activeStepByTab, currentTabIndex) => activeStepByTab[currentTabIndex] || 0,
);

export const selectCurrentTabStepStatus = createSelector(
  [selectStepStatusByTab, selectCurrentTabIndex],
  (stepStatus, currentTabIndex) => stepStatus[currentTabIndex] || {},
);

// Lead Profile: Get Current Applicant
export const selectCurrentLeadProfileApplicant = createSelector(
  [selectLeadProfileApplicants, selectCurrentTabIndex],
  (applicants, currentTabIndex) => applicants[currentTabIndex],
);

// Smart selector: Get "active" response ID (lead profile or not)
export const selectActiveResponseId = createSelector(
  [
    selectIsLeadProfile,
    selectLeadProfileApplicants,
    selectCurrentTabIndex,
    (state: RootState) => state.navigation.leadProfileResponseId,
    (state: RootState) => state.application.activeResponse?.responseId,
  ],
  (
    isLeadProfile,
    applicants,
    currentTabIndex,
    leadProfileResponseId,
    applicationActiveResponseId,
  ) =>
    isLeadProfile
      ? (leadProfileResponseId ?? applicants?.[currentTabIndex]?.responseId)
      : applicationActiveResponseId,
);

// Application-wide IDs
export const selectActiveApplicationId = createSelector(
  [(state: RootState) => state.application.applicant?.currentResponse.applicationId],
  (applicationId) => applicationId,
);

export const selectActiveVersionId = createSelector(
  [(state: RootState) => state.application.applicant?.currentResponse?.versionId],
  (versionId) => versionId,
);

// Branding/media selector - directly return media from selectBrandingMedia which already has fallback
export const brandingMediaSelector = selectBrandingMedia;

// Combination selector for branding + application + response
export const applicationBrandingResponseSelector = createSelector(
  [selectApplication, selectBrandingMedia, selectResponse],
  (application, branding, response) => ({
    ...application,
    ...response,
    media: branding,
  }),
);
